"use client";

import { useEffect, useSyncExternalStore } from "react";
import { THEME_COLOR, THEME_STORAGE_KEY } from "@/lib/theme";

/**
 * Alternador de tema claro/escuro do site público.
 * O tema vive no atributo `data-theme` do <html> (escrito antes da pintura pelo
 * script THEME_INIT_SCRIPT (src/lib/theme.ts) no layout, para não piscar) e a escolha fica em
 * localStorage. Sem escolha salva, o site segue o padrão da marca: escuro.
 */
type Theme = "dark" | "light";

function subscribe(cb: () => void) {
  const mo = new MutationObserver(cb);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  return () => mo.disconnect();
}
const getTheme = (): Theme => (document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark");
const getServerTheme = (): Theme => "dark";

export function ThemeToggle({ className = "" }: { className?: string }) {
  const theme = useSyncExternalStore(subscribe, getTheme, getServerTheme);
  const next: Theme = theme === "light" ? "dark" : "light";

  // Se o React refizer o <html> (ex.: erro de hidratação), o atributo volta ao
  // padrão do servidor ("dark"). Reaplica a escolha salva sempre que divergir.
  useEffect(() => {
    const root = document.documentElement;
    const keep = () => {
      try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        if ((saved === "light" || saved === "dark") && root.getAttribute("data-theme") !== saved) {
          root.setAttribute("data-theme", saved);
        }
      } catch {
        /* sem localStorage: nada a reaplicar */
      }
    };
    keep();
    const mo = new MutationObserver(keep);
    mo.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => mo.disconnect();
  }, []);

  function apply() {
    const root = document.documentElement;
    root.classList.add("theme-switching");
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* modo privado: segue sem persistir */
    }
    root.setAttribute("data-theme", next);
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[next]);
    window.setTimeout(() => root.classList.remove("theme-switching"), 400);
  }

  return (
    <button
      type="button"
      className={`theme-toggle ${className}`.trim()}
      onClick={apply}
      aria-label={next === "light" ? "Ativar tema claro" : "Ativar tema escuro"}
      title={next === "light" ? "Tema claro" : "Tema escuro"}
      data-theme-current={theme}
    >
      <svg className="theme-toggle__sun" viewBox="0 0 24 24" aria-hidden>
        <circle cx="12" cy="12" r="4.2" fill="currentColor" />
        <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M5.3 18.7l1.6-1.6M17.1 6.9l1.6-1.6" />
        </g>
      </svg>
      <svg className="theme-toggle__moon" viewBox="0 0 24 24" aria-hidden>
        <path d="M20 14.6A8.5 8.5 0 0 1 9.4 4a8.5 8.5 0 1 0 10.6 10.6Z" fill="currentColor" />
      </svg>
    </button>
  );
}
